import {
    Box,
    TextField,
    Typography,
    FormControlLabel,
    RadioGroup,
    Radio,
    FormControl,
    FormLabel,
    FormHelperText,
  } from "@mui/material";
  import FormMonitoring from "components/FormMonitoring";
  import {
    CommonErrorsTexts,
    CommonTexts,
    ConfigFormsLabels,
    scheduledForms,
  } from "locale/es";
  import { useEffect, useState } from "react";
  import { useDispatch, useSelector } from "react-redux";
  import SingleChoiceDropdown from "components/Dropdowns/SingleChoiceDropdown";
  import Loader from "components/Loader/Loader";
  import ErrorPage from "pages/ErrorPage";
  import { AppColors } from "resources/AppColors";
  import { makeStyles } from "@mui/styles";
  import { z } from "zod";
  import { Controller, useForm } from "react-hook-form";
  import { zodResolver } from "@hookform/resolvers/zod";
  import { initializeBusinessPackageOptions } from "redux/reducers/dataAppAndPackageReducer";
  import { zodInputNumberPipe } from "types";
  import { initializeStudy } from "redux/reducers/studyReducer";
  
  const useStyles = makeStyles((theme) => ({
    textFieldLabel: {
      "& .MuiOutlinedInput-root": {
        "& fieldset": {
          borderColor: AppColors.PRIMARY,
        },
      },
      "& .MuiInputBase-root": {
        color: AppColors.PRIMARY,
      },
      "& .MuiInputLabel-root": {
        color: AppColors.PRIMARY,
      },
      color: AppColors.PRIMARY,
      backgroundColor: AppColors.BOX_FILL,
    },
  }));
  
  export const radioButtonOptions = [
    { label: scheduledForms.IMPORTANT, value: "IMPORTANT" },
    { label: scheduledForms.REQUIRED, value: "REQUIRED" },
    { label: scheduledForms.NOT_REQUIRED, value: "NOT_REQUIRED" },
  ];
  
  
  const descriptionType = z.string().superRefine((val, ctx) => {
    if (val.length === 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.too_small,
        minimum: 2,
        type: "string",
        inclusive: true,
        message: scheduledForms.formInputErrors.description,
      });
    }
  
    return val;
  });
  
  const daysOpenType = zodInputNumberPipe(
    z.number().int().gt(0).max(999999).optional()
  );
  
  const NewScheduledFormSchema = z
    .object({
      studiesFormId: z.object({
        id: z.number(),
        title: z.string(),
      }),
      type: z.string().min(1, CommonErrorsTexts.required),
      slug: z.string().min(1, scheduledForms.formInputErrors.form),
      businessPackage: z.string().min(1, scheduledForms.formInputErrors.package),
      businessLabel: z
        .string({ required_error: CommonErrorsTexts.required })
        .min(2, scheduledForms.formInputErrors.tag),
      description: descriptionType,
      daysOpen: daysOpenType,
      title: z.string().optional(),
    })
    .transform((data) => ({
      ...data,
      studiesFormId: data.studiesFormId.id,
      businessPackage: {
        id: data.businessPackage,
      },
    }));
  
  const AddOrEditScheduledForm = ({
    entityId,
    selectedFormId,
    isEdit = false,
    setShowModal,
    dataForm,
    onSubmit,
  }) => {
    const [formError, setFormError] = useState("");
    const [formData, setFormData] = useState({
      slug: "",
      title: "",
      description: "",
    });
  
    const selectedCenterStudyId = useSelector(({ center }) => center)?.data
      ?.studyId;
  
    const {
      data: studyData,
      loading: studyLoading,
      error: studyError,
    } = useSelector(({ study }) => study);
  
    const selectedCenter = useSelector(({ center }) => center)?.data;

    const centerId = selectedCenter.id;

    const businessCenter = selectedCenter?.businessPackage?.id;
  
    const dispatch = useDispatch();
  
    const classes = useStyles();
  
    useEffect(() => {
      if (selectedCenterStudyId && !studyData) {
        dispatch(initializeStudy(selectedCenterStudyId));
      }
    }, [selectedCenterStudyId, studyData, dispatch]);
  
    
    const { businessPackageOptions } = useSelector(
      ({ optionsAppAndBusiness }) => optionsAppAndBusiness
    );
  
    useEffect(() => {
      if (!businessPackageOptions) {
        dispatch(initializeBusinessPackageOptions());
      }
    }, [businessPackageOptions, dispatch]);
  
    const formToEdit = dataForm?.find((form) => form.id === selectedFormId);
  
    useEffect(() => {
      if (formToEdit) {
        setFormData((prevData) => ({
          ...prevData,
          title: formToEdit.title,
          description: formToEdit.description,
        }));
      }
    }, [selectedFormId, dataForm]);
  
    const { handleSubmit, control, reset, clearErrors } = useForm({
      mode: "onBlur",
      reValidateMode: "onChange",
      resolver: zodResolver(NewScheduledFormSchema),
      resetOptions: {
        keepDefaultValues: true,
      },
      defaultValues: {
        type: formToEdit?.type || "",
        studiesFormId: studyData,
        slug: formToEdit?.slug || "",
        description: formToEdit?.description || "",
        businessLabel: formToEdit?.businessLabel || "",
        businessPackage: formToEdit?.businessPackage?.id || businessCenter || "",
        title: formToEdit?.title || "",
        daysOpen: formToEdit?.daysOpen || "",
      },
    });
  
    const handleSlugChange = (selectedSlug) => {
      const selectedForm = studyData?.forms.find(
        (form) => form.slug === selectedSlug.target.value
      );
  
      const title = selectedForm ? selectedForm.name : "";
      const description = selectedForm ? selectedForm.description : "";
  
      setFormData((prevData) => ({
        ...prevData,
        slug: selectedSlug.target.value,
        title: title,
        description: description,
      }));
    };
  
    const handleTitleChange = (e) => {
      setFormData((prevData) => ({
        ...prevData,
        title: e.target.value,
      }));
    };
  
    const handleDescriptionChange = (e) => {
      setFormData((prevData) => ({
        ...prevData,
        description: e.target.value,
      }));
    };
  
    async function handleOnConfirmSubmit(data) {
      try {
        const formObject = {
          ...data,
          slug: data.slug.toString(),
          studiesFormId: data.studiesFormId.toString(),
          center: { id: centerId },
          title: formData.title,
          description: formData.description,
          id: isEdit ? formToEdit.id : null,
        };
  
        const formId = formObject.id;
  
        if (!isEdit) {
          await dispatch(onSubmit(formObject));
          setShowModal(false);
        } else {
          await dispatch(onSubmit(formObject, formId));
          setShowModal(false);
        }
      } catch (error) {
        setFormError(error);
      }
    }
  
    if (studyLoading) {
      return <Loader message={"Loading study and forms..."} pt={1} />;
    }
  
    if (!studyData) {
      return <ErrorPage message='This center has no associated study' />;
    }
  
    if (!businessPackageOptions) return null;
  
    return (
      <FormMonitoring
        submitForm={handleSubmit(handleOnConfirmSubmit)}
        resetFormInputs={reset}
        isEdit={isEdit}
      >
        <Box sx={{ display: "flex", mb: 2, ml: 2 }}>
          <Controller
            control={control}
            name='type'
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <FormControl error={Boolean(error)} component='fieldset'>
                <FormLabel
                  sx={{ color: AppColors.PRIMARY }}
                  id='demo-row-radio-buttons-group-label'
                  required
                >
                  <Typography variant='subtitle_bold' mt={2}>
                    {scheduledForms.PRIORITY}
                  </Typography>
                </FormLabel>
                <RadioGroup
                  row
                  aria-labelledby='demo-row-radio-buttons-group-label'
                  name='row-radio-buttons-group'
                  value={value}
                  onChange={onChange}
                >
                  {radioButtonOptions.map((option) => (
                    <FormControlLabel
                      key={option.label}
                      value={option.value}
                      control={<Radio />}
                      label={option.label}
                    />
                  ))}
                </RadioGroup>
                <FormHelperText
                  id='my-helper-text'
                  error={Boolean(error)}
                  sx={{ mt: 0, pl: 2 }}
                >
                  {error ? error.message : ""}
                </FormHelperText>
              </FormControl>
            )}
          />
        </Box>
        <Controller
          control={control}
          name='studiesFormId'
          render={({ field: { value } }) => (
            <TextField
              value={value.title}
              type='text'
              disabled
              label={ConfigFormsLabels.STUDY_NAME}
              margin='normal'
              variant='outlined'
              className={classes.textFieldLabel}
            />
          )}
        />
        <Controller
          control={control}
          name='slug'
          render={({
            field: { onChange, onBlur, value },
            fieldState: { error },
          }) => (
            <>
              <SingleChoiceDropdown
                isScheduledForm={true}
                labelRequired={true}
                label={scheduledForms.FORM}
                value={value}
                onChange={(selectedSlug) => {
                  onChange(selectedSlug);
                  handleSlugChange(selectedSlug);
                }}
                onBlur={onBlur}
                items={studyData?.forms}
                error={error}
              />
            </>
          )}
        />
        <Controller
          control={control}
          name='title'
          render={({
            field: { onChange, onBlur, value },
            fieldState: { error },
          }) => (
            <TextField
              type='text'
              label={scheduledForms.FORM_TITLE}
              margin='normal'
              variant='outlined'
              className={classes.textFieldLabel}
              error={Boolean(error)}
              helperText={error?.message ?? ""}
              value={formData.title}
              onChange={handleTitleChange}
              onBlur={onBlur}
            />
          )}
        />
        <Controller
          control={control}
          name='description'
          render={({
            field: { onChange, onBlur, value },
            fieldState: { error },
          }) => (
            <TextField
              type='text'
              value={formData.description}
              onChange={handleDescriptionChange}
              onBlur={onBlur}
              label={CommonTexts.DESCRIPTION}
              margin='normal'
              variant='outlined'
              className={classes.textFieldLabel}
              error={Boolean(error)}
              helperText={error?.message ?? ""}
            />
          )}
        />
        <Controller
          control={control}
          name='businessLabel'
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              type='text'
              InputLabelProps={{
                required: true,
              }}
              disabled={isEdit}
              label={scheduledForms.PACKAGE_LABEL}
              margin='normal'
              variant='outlined'
              className={classes.textFieldLabel}
              error={Boolean(error)}
              helperText={error?.message ?? ""}
            />
          )}
        />
        <Controller
          control={control}
          name='businessPackage'
          render={({
            field: { onChange, onBlur, value },
            fieldState: { error },
          }) => (
            <SingleChoiceDropdown
              label={scheduledForms.PACKAGE}
              labelRequired={true}
              value={value}
              onChange={onChange}
              onBlur={onBlur}
              items={businessPackageOptions}
              error={error}
            />
          )}
        />
        <Controller
          control={control}
          name='daysOpen'
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              type='number'
              label={scheduledForms.DAYS_OPEN}
              variant='outlined'
              className={classes.textFieldLabel}
              sx={{ mt: 2, mb: 2 }}
              inputProps={{
                min: 1,
                max: 999999,
              }}
              error={Boolean(error)}
              helperText={error?.message ?? ""}
            />
          )}
        />
      </FormMonitoring>
    );
  };
  
  export default AddOrEditScheduledForm;
  
  